import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TrialCountdown from '../components/TrialCountdown';
import { 
  Users, 
  MessageCircle, 
  Heart, 
  Shield, 
  Star, 
  Clock, 
  TrendingUp,
  Brain,
  Sparkles,
  Zap,
  Target,
  Activity,
  BarChart3,
  Eye,
  EyeOff,
  Search,
  Filter,
  SortAsc,
  SortDesc,
  Plus,
  ArrowRight,
  AlertTriangle,
  CheckCircle,
  Info,
  ChevronRight,
  Flower,
  HeartPulse,
  Cloud,
  Wind
} from 'lucide-react';
import api from '../utils/api';

const PeerSupport = () => {
  const [forums, setForums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedForum, setSelectedForum] = useState(null);
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [memberships, setMemberships] = useState({});
  const [loadingMemberships, setLoadingMemberships] = useState(true);
  const navigate = useNavigate();

  // Icon mapping for different specialties
  const getIcon = (iconName) => {
    const iconMap = {
      'users': Users,
      'heart': Heart,
      'shield': Shield,
      'flower': Flower,
      'zap': Zap,
      'star': Star,
      'rings': Heart,
      'heart-pulse': HeartPulse,
      'cloud': Cloud,
      'wind': Wind
    };
    return iconMap[iconName] || Users;
  };

  // Color mapping for different specialties
  const getColorClasses = (color) => {
    const colorMap = {
      'blue': 'bg-blue-500 text-white',
      'pink': 'bg-pink-500 text-white',
      'purple': 'bg-purple-500 text-white',
      'gray': 'bg-gray-500 text-white',
      'orange': 'bg-orange-500 text-white',
      'green': 'bg-green-500 text-white',
      'red': 'bg-red-500 text-white',
      'yellow': 'bg-yellow-500 text-black',
      'indigo': 'bg-indigo-500 text-white',
      'teal': 'bg-teal-500 text-white'
    };
    return colorMap[color] || 'bg-blue-500 text-white';
  };

  useEffect(() => {
    fetchForums();
    fetchMemberships();
  }, []);

  const fetchForums = async () => {
    try {
      console.log('=== PEER SUPPORT PAGE DEBUG ===');
      console.log('Fetching forums...');
      setLoading(true);
      
      const response = await api.get('/peer-support/forums');
      console.log('Forums response:', response.data);
      setForums(response.data.forums || []);
      console.log('Forums data:', response.data.forums);
    } catch (err) {
      console.error('Error fetching forums:', err);
      setError('Failed to load peer support forums. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchMemberships = async () => {
    try {
      setLoadingMemberships(true);
      const response = await api.get('/peer-support/user/memberships');
      const membershipMap = {};
      response.data.memberships.forEach(membership => {
        membershipMap[membership.forumId] = membership;
      });
      setMemberships(membershipMap);
    } catch (err) {
      console.error('Error fetching memberships:', err);
    } finally {
      setLoadingMemberships(false);
    }
  };

  const handleJoinDiscussion = async (forum) => {
    try {
      // Check if user is already a member
      if (memberships[forum.id]) {
        // User is already a member, go directly to forum
        navigate(`/app/peer-support/forum/${forum.id}`, { 
          state: { forum: forum } 
        });
        return;
      }

      // User is not a member, show guidelines and join process
      const response = await api.get(`/peer-support/forums/${forum.id}`);
      setSelectedForum(response.data.forum);
      setShowGuidelines(true);
    } catch (err) {
      console.error('Error fetching forum details:', err);
      // Fallback to basic forum data if detailed fetch fails
      setSelectedForum(forum);
      setShowGuidelines(true);
    }
  };

  const handleAcceptGuidelines = async () => {
    if (selectedForum) {
      try {
        // Join the forum
        await api.post(`/peer-support/forums/${selectedForum.id}/join`);
        
        // Refresh memberships
        await fetchMemberships();
        
        // Navigate to forum
        navigate(`/app/peer-support/forum/${selectedForum.id}`, { 
          state: { forum: selectedForum } 
        });
      } catch (err) {
        console.error('Error joining forum:', err);
        alert(err.response?.data?.error || 'Failed to join forum. Please try again.');
      }
    }
  };

  const handleCloseGuidelines = () => {
    setShowGuidelines(false);
    setSelectedForum(null);
  };

  const handleLeaveForum = async (forum) => {
    if (!window.confirm(`Are you sure you want to leave the "${forum.name}" forum? You can rejoin anytime.`)) {
      return;
    }

    try {
      await api.post(`/peer-support/forums/${forum.id}/leave`);
      
      // Refresh memberships
      await fetchMemberships();
      
      // Refresh forums to update member count
      await fetchForums();
      
      alert('Successfully left the forum.');
    } catch (err) {
      console.error('Error leaving forum:', err);
      alert(err.response?.data?.error || 'Failed to leave forum. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center">
            <AlertTriangle className="h-6 w-6 text-red-500 mr-3" />
            <h3 className="text-lg font-semibold text-red-800">Error Loading Forums</h3>
          </div>
          <p className="text-red-700 mt-2">{error}</p>
          <button 
            onClick={fetchForums}
            className="mt-4 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Trial Countdown */}
      <TrialCountdown />
      
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Peer Support Community</h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Connect with others on similar journeys in a safe, moderated environment. 
          Each forum is dedicated to a specific area of mental health support.
        </p>
      </div>

      {/* Community Guidelines Banner */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 mb-8">
        <div className="flex items-start">
          <Shield className="h-8 w-8 text-blue-600 mt-1 mr-4 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-blue-900 mb-2">Community Guidelines</h3>
            <p className="text-blue-800 mb-4">
              Our peer support community is built on safety, respect, and mutual support. 
              Each forum has specific guidelines designed to create a supportive environment for everyone.
            </p>
            <div className="grid md:grid-cols-2 gap-4 text-sm text-blue-700">
              <div className="flex items-center">
                <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                <span>24/7 moderation and crisis detection</span>
              </div>
              <div className="flex items-center">
                <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                <span>Anonymous posting options available</span>
              </div>
              <div className="flex items-center">
                <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                <span>Professional crisis resources provided</span>
              </div>
              <div className="flex items-center">
                <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                <span>Evidence-based support guidelines</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Forums Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
        {forums.map((forum) => {
          const IconComponent = getIcon(forum.icon);
          const colorClasses = getColorClasses(forum.color);
          
          return (
            <div key={forum.id} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden">
              {/* Forum Header */}
              <div className={`${colorClasses} p-6`}>
                <div className="flex items-center justify-between mb-3">
                  <IconComponent className="h-8 w-8" />
                  <span className="text-xs font-medium bg-white bg-opacity-20 px-2 py-1 rounded-full">
                    {forum.specialty}
                  </span>
                </div>
                <h3 className="text-xl font-bold mb-2">{forum.name}</h3>
                <p className="text-sm opacity-90 line-clamp-3">{forum.description}</p>
              </div>

              {/* Forum Stats */}
              <div className="p-6">
                <div className="flex items-center justify-between mb-4 text-sm text-gray-600">
                  <div className="flex items-center">
                    <Users className="h-4 w-4 mr-1" />
                    <span>{forum.stats.totalMembers.toLocaleString()} members</span>
                  </div>
                  <div className="flex items-center">
                    <MessageCircle className="h-4 w-4 mr-1" />
                    <span>{forum.stats.totalPosts.toLocaleString()} posts</span>
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-2 mb-6">
                  {forum.settings.allowAnonymous && (
                    <div className="flex items-center text-xs text-gray-500">
                      <Shield className="h-3 w-3 mr-1" />
                      <span>Anonymous posting available</span>
                    </div>
                  )}
                  <div className="flex items-center text-xs text-gray-500">
                    <Info className="h-3 w-3 mr-1" />
                    <span>Moderated environment</span>
                  </div>
                </div>

                {/* Action Button */}
                {memberships[forum.id] ? (
                  <div className="space-y-2">
                    <button
                      onClick={() => handleJoinDiscussion(forum)}
                      className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center"
                    >
                      Enter Forum
                      <ChevronRight className="h-4 w-4 ml-2" />
                    </button>
                    <button
                      onClick={() => handleLeaveForum(forum)}
                      className="w-full bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors font-medium flex items-center justify-center"
                    >
                      Leave Forum
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => handleJoinDiscussion(forum)}
                    className="w-full bg-gray-900 text-white py-3 px-4 rounded-lg hover:bg-gray-800 transition-colors font-medium flex items-center justify-center"
                  >
                    Join Discussion
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Safety Information */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
        <div className="flex items-start">
          <AlertTriangle className="h-8 w-8 text-amber-600 mt-1 mr-4 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-amber-900 mb-2">Important Safety Information</h3>
            <p className="text-amber-800 mb-4">
              This peer support community is not a substitute for professional mental health care. 
              If you're experiencing a crisis or need immediate help, please contact emergency services.
            </p>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div className="bg-white rounded-lg p-4 border border-amber-200">
                <h4 className="font-semibold text-amber-900 mb-2">Crisis Support</h4>
                <p className="text-amber-800 mb-2">National Suicide Prevention Lifeline</p>
                <p className="font-mono text-amber-900">988</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-amber-200">
                <h4 className="font-semibold text-amber-900 mb-2">Text Support</h4>
                <p className="text-amber-800 mb-2">Crisis Text Line</p>
                <p className="font-mono text-amber-900">Text HOME to 741741</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Guidelines Modal */}
      {showGuidelines && selectedForum && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">
                  Community Guidelines - {selectedForum.name}
                </h2>
                <button
                  onClick={handleCloseGuidelines}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">About This Forum</h3>
                <p className="text-gray-700">
                  {selectedForum.guidelines?.description || 
                   'This forum provides a supportive environment for peer discussion and mutual support.'}
                </p>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Community Rules</h3>
                <div className="space-y-4">
                  {selectedForum.guidelines?.rules ? (
                    selectedForum.guidelines.rules.map((rule, index) => (
                      <div key={index} className="border-l-4 border-blue-500 pl-4">
                        <h4 className="font-semibold text-gray-900 mb-1">{rule.title}</h4>
                        <p className="text-gray-700 text-sm">{rule.description}</p>
                        <span className={`inline-block mt-2 px-2 py-1 rounded-full text-xs font-medium ${
                          rule.importance === 'high' ? 'bg-red-100 text-red-800' :
                          rule.importance === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {rule.importance} priority
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="border-l-4 border-blue-500 pl-4">
                      <h4 className="font-semibold text-gray-900 mb-1">General Community Guidelines</h4>
                      <p className="text-gray-700 text-sm">
                        Be respectful, supportive, and kind to all members. This is a safe space for peer support and shared experiences.
                      </p>
                      <span className="inline-block mt-2 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        medium priority
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {selectedForum.guidelines?.crisisResources && selectedForum.guidelines.crisisResources.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Crisis Resources</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {selectedForum.guidelines.crisisResources.map((resource, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-1">{resource.title}</h4>
                        <p className="text-gray-700 text-sm mb-2">{resource.description}</p>
                        <p className="font-mono text-blue-600">{resource.contact}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <Info className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-1">By joining this discussion, you agree to:</h4>
                    <ul className="text-blue-800 text-sm space-y-1">
                      <li>• Follow all community guidelines and rules</li>
                      <li>• Respect the privacy and anonymity of other members</li>
                      <li>• Not provide medical, legal, or professional advice</li>
                      <li>• Report any concerning content to moderators</li>
                      <li>• Seek professional help if you're in crisis</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  onClick={handleCloseGuidelines}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAcceptGuidelines}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  I Agree & Join Discussion
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {forums.length === 0 && !loading && !error && (
        <div className="text-center py-12">
          <MessageCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No forums available</h3>
          <p className="text-gray-500">Peer support forums are currently being set up. Please check back later.</p>
        </div>
      )}
    </div>
  );
};

export default PeerSupport; 